import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useDarkClasses } from '../../hooks/useDarkClasses';
interface CartDropdownProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  cartItemCount: number;
  cartButtonRef: React.RefObject<HTMLButtonElement>;
}
const CartDropdown = ({
  isOpen,
  setIsOpen,
  cartItemCount,
  cartButtonRef
}: CartDropdownProps) => {
  const dk = useDarkClasses();
  const dropdownRef = useRef<HTMLDivElement>(null);
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node) && cartButtonRef.current && !cartButtonRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [setIsOpen, cartButtonRef]);
  // Close dropdown when pressing Escape
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };
    document.addEventListener('keydown', handleEscapeKey);
    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [setIsOpen]);
  if (!isOpen) return null;
  return <div ref={dropdownRef} className={`absolute right-0 w-80 mt-2 rounded-lg shadow-lg py-2 z-50 ${dk.isDark ? 'bg-gray-800' : 'bg-white'}`} role="dialog" aria-modal="true" aria-label="Shopping Cart" data-id="element-1928" data-mp="1rmahtc8wferu">
      <div className={`px-4 py-2 border-b ${dk.isDark ? 'border-gray-700' : 'border-gray-100'}`} data-id="element-1929" data-mp="1hra66d7gjgka">
        <h3 className={`font-medium ${dk.textPrimary}`} data-id="element-1930" data-mp="3c36ongnx9c37">
          Your Cart ({cartItemCount})
        </h3>
      </div>
      <div className="max-h-72 overflow-y-auto" data-id="element-1931" data-mp="3h29v95jjvqda">
        {cartItemCount > 0 ? <>
            <div className={`px-4 py-3 border-b ${dk.isDark ? 'border-gray-700' : 'border-gray-100'}`} data-id="element-1932" data-mp="3fldzkvac52rc">
              <div className="flex items-center" data-id="element-1933" data-mp="3ak6pgpkpzrhi">
                <div className={`h-12 w-8 rounded flex-shrink-0 ${dk.isDark ? 'bg-gray-700' : 'bg-gray-200'}`} data-id="element-1934" data-mp="35mewds8c9zbq"></div>
                <div className="ml-3 flex-1" data-id="element-1935" data-mp="31mo0xb7vav0v">
                  <div className="flex justify-between" data-id="element-1936" data-mp="25omh29mz24r">
                    <p className={`text-sm font-medium ${dk.textPrimary}`} data-id="element-1937" data-mp="256gvf133bk6">
                      The Silent Echo
                    </p>
                    <p className={`text-sm font-medium ${dk.textPrimary}`} data-id="element-1938" data-mp="3olaqz4o40fq3">
                      $14.99
                    </p>
                  </div>
                  <p className={`text-xs ${dk.textMuted}`} data-id="element-1939" data-mp="3kjfp9z29o0bs">Paperback</p>
                </div>
              </div>
            </div>
            {cartItemCount > 1 && <div className={`px-4 py-3 border-b ${dk.isDark ? 'border-gray-700' : 'border-gray-100'}`} data-id="element-1940" data-mp="g5h8wle4qw6x">
                <div className="flex items-center" data-id="element-1941" data-mp="1yubrhm04824i">
                  <div className={`h-12 w-8 rounded flex-shrink-0 ${dk.isDark ? 'bg-gray-700' : 'bg-gray-200'}`} data-id="element-1942" data-mp="23r26e606oroc"></div>
                  <div className="ml-3 flex-1" data-id="element-1943" data-mp="27v9y8c1xunqs">
                    <div className="flex justify-between" data-id="element-1944" data-mp="2csif89bcolqg">
                      <p className={`text-sm font-medium ${dk.textPrimary}`} data-id="element-1945" data-mp="2cpw11qtyf7z9">
                        Beyond the Veil
                      </p>
                      <p className={`text-sm font-medium ${dk.textPrimary}`} data-id="element-1946" data-mp="2mpeh3w6qu9m1">
                        $9.99
                      </p>
                    </div>
                    <p className={`text-xs ${dk.textMuted}`} data-id="element-1947" data-mp="2viqf8ijr6w2f">eBook</p>
                  </div>
                </div>
              </div>}
          </> : <div className={`px-4 py-6 text-center ${dk.textMuted}`} data-id="element-1948" data-mp="30jnuonddpc08">
            Your cart is empty
          </div>}
      </div>
      <div className={`px-4 py-3 border-t ${dk.isDark ? 'border-gray-700' : 'border-gray-100'}`} data-id="element-1949" data-mp="2rtxfjllz9f8x">
        <div className="flex justify-between mb-3" data-id="element-1950" data-mp="2mvnjz9wcccms">
          <span className={`font-medium ${dk.textSecondary}`} data-id="element-1951" data-mp="2mt1cfa9g9x4k">Subtotal</span>
          <span className={`font-medium ${dk.textPrimary}`} data-id="element-1952" data-mp="31ip55n3kfngo">
            $
            {cartItemCount > 1 ? '24.98' : cartItemCount > 0 ? '14.99' : '0.00'}
          </span>
        </div>
        <Link to="/checkout" className={`block w-full bg-indigo-500 text-white text-center py-2 px-4 rounded-md font-medium ${cartItemCount === 0 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-indigo-600'}`} onClick={() => {
        if (cartItemCount > 0) {
          setIsOpen(false);
        }
      }} aria-disabled={cartItemCount === 0} data-id="element-1953" data-mp="28we122gr34f4">
          Proceed to Checkout
        </Link>
        <button onClick={() => setIsOpen(false)} className={`block w-full text-center py-2 px-4 mt-2 text-sm ${dk.isDark ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-1954" data-mp="3uaijf902dyv8">
          Continue Shopping
        </button>
      </div>
    </div>;
};
export default CartDropdown;